import { streamdownRehypePluginsWithImageRefs } from '@heidi/answer-kit';
import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { Label } from '@heidi/design-system/label';
import { Textarea } from '@heidi/design-system/textarea';
import { cn } from '@heidi/utils';
import { Image as ImageIcon, Mail, Pencil } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Streamdown } from 'streamdown';

import { InlineApprovalActions } from './inline-approval-actions';

export interface MailComposeValues {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
}

export type MailComposeField = keyof MailComposeValues;

export interface MailComposeApprovalLayoutProps {
  /** Card root test id. */
  testId: string;
  /** Prefix for the per-field test ids (`<prefix>-to`, `<prefix>-body`, …). */
  fieldTestIdPrefix: string;
  title: string;
  values: MailComposeValues;
  onChange: (field: MailComposeField, value: string) => void;
  /** Replaces the To/Cc/Bcc/Subject rows (e.g. a reply's thread reference). */
  headerOverride?: ReactNode;
  bodyPlaceholder?: string;
  /** Rendered between the body and the actions (e.g. an attachment note). */
  footnote?: ReactNode;
  isBusy: boolean;
  canApprove: boolean;
  approveLabel: string;
  pendingVerdict: 'approve' | 'deny' | null;
  onApprove: () => void;
  onDeny: () => void;
}

function ComposeRow({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-border px-3 py-1">
      <Label htmlFor={id} className="w-16 shrink-0 text-xs text-text-secondary">
        {label}
      </Label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

const composeInputClass =
  'h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0';
const composeWrapperClass = 'rounded-none border-0 bg-transparent px-0 shadow-none';

/** Mirrors the sent email: softbreaks stay visible, tables get cell borders. */
const bodyPreviewClass = cn(
  'min-w-0 flex-1 text-pretty text-sm text-text-primary',
  '[&_li]:whitespace-pre-line [&_p]:whitespace-pre-line [&_strong]:font-semibold',
  '[&_ol]:list-decimal [&_ol]:ps-5 [&_ul]:list-disc [&_ul]:ps-5',
  '[&_table]:my-1 [&_table]:border-collapse [&_table]:tabular-nums',
  '[&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-start [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1'
);

/**
 * Names an image the sent email carries without loading it: the preview never
 * fetches a markdown image (HEI2-134), but the approver still sees it is there.
 */
function MailImageRef({ alt }: { alt?: string }) {
  const intl = useIntl();
  const name = alt?.trim();
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 align-middle text-xs text-text-secondary">
      <ImageIcon className="size-3.5 shrink-0" aria-hidden />
      {name
        ? intl.formatMessage(
            { defaultMessage: 'Image: {name}', id: 'zglwcocKam' },
            { name }
          )
        : intl.formatMessage({ defaultMessage: 'Image', id: '+0zv6gS/c6' })}
    </span>
  );
}

const mailPreviewComponents = { 'image-ref': MailImageRef };

/** An email body as it sends: softbreaks kept, images named instead of loaded. */
export function MailBodyMarkdown({ body, testId }: { body: string; testId: string }) {
  return (
    <div className={bodyPreviewClass} data-testid={testId}>
      <Streamdown
        controls={false}
        codeBlockMaxHeight={0}
        tableMaxHeight={0}
        className="space-y-2 *:first:mt-0 *:last:mb-0 [&_p]:my-0"
        components={mailPreviewComponents}
        rehypePlugins={streamdownRehypePluginsWithImageRefs}
      >
        {body}
      </Streamdown>
    </div>
  );
}

function MailBodyPreview({
  body,
  testId,
  editLabel,
  disabled,
  onEdit,
}: {
  body: string;
  testId: string;
  editLabel: string;
  disabled: boolean;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <MailBodyMarkdown body={body} testId={testId} />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        onClick={onEdit}
        disabled={disabled}
        aria-label={editLabel}
        data-testid={`${testId}-edit`}
      >
        <Pencil />
      </Button>
    </div>
  );
}

/** Email-client style approval card: editable To / Cc / Bcc / Subject / body. */
export function MailComposeApprovalLayout({
  testId,
  fieldTestIdPrefix,
  title,
  values,
  onChange,
  headerOverride,
  bodyPlaceholder,
  footnote,
  isBusy,
  canApprove,
  approveLabel,
  pendingVerdict,
  onApprove,
  onDeny,
}: MailComposeApprovalLayoutProps): JSX.Element {
  const intl = useIntl();
  const fieldId = useId();
  const [isEditingBody, setIsEditingBody] = useState(false);
  const showBodyPreview = !isEditingBody && values.body.trim() !== '';

  const rows: { field: Exclude<MailComposeField, 'body'>; label: string }[] = [
    {
      field: 'to',
      label: intl.formatMessage({ defaultMessage: 'To', id: '9j3hXO4Ojb' }),
    },
    {
      field: 'cc',
      label: intl.formatMessage({ defaultMessage: 'Cc', id: 'J1CYkLdqdy' }),
    },
    {
      field: 'bcc',
      label: intl.formatMessage({ defaultMessage: 'Bcc', id: 'qLl3P2UOlZ' }),
    },
    {
      field: 'subject',
      label: intl.formatMessage({ defaultMessage: 'Subject', id: 'LLtKhppiyE' }),
    },
  ];

  return (
    <div
      className="flex w-full flex-col overflow-hidden rounded-xl border border-border bg-surface"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        <Mail className="size-4 shrink-0 text-text-secondary" />
        <p className="min-w-0 text-pretty text-sm font-medium text-text-primary">
          {title}
        </p>
      </div>
      {headerOverride ??
        rows.map(({ field, label }) => (
          <ComposeRow key={field} id={`${fieldId}-${field}`} label={label}>
            <Input
              id={`${fieldId}-${field}`}
              value={values[field]}
              onChange={(event) => onChange(field, event.target.value)}
              disabled={isBusy}
              density="compact"
              className={composeInputClass}
              wrapperClassName={composeWrapperClass}
              data-testid={`${fieldTestIdPrefix}-${field}`}
            />
          </ComposeRow>
        ))}
      <div className="px-3 py-2">
        {showBodyPreview ? (
          <MailBodyPreview
            body={values.body}
            testId={`${fieldTestIdPrefix}-body-preview`}
            editLabel={intl.formatMessage({
              defaultMessage: 'Edit email body',
              id: '/crak3Y3SA',
            })}
            disabled={isBusy}
            onEdit={() => setIsEditingBody(true)}
          />
        ) : (
          <Textarea
            id={`${fieldId}-body`}
            value={values.body}
            onChange={(event) => onChange('body', event.target.value)}
            onBlur={() => setIsEditingBody(false)}
            autoFocus={isEditingBody}
            placeholder={bodyPlaceholder}
            minRows={6}
            maxRows={14}
            autoResize
            disabled={isBusy}
            className="rounded-none border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
            data-testid={`${fieldTestIdPrefix}-body`}
          />
        )}
      </div>
      {footnote}
      <div className="flex justify-start border-t border-border px-3 py-2">
        <InlineApprovalActions
          className="justify-start"
          onApprove={onApprove}
          onDeny={onDeny}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
          approveDisabled={!canApprove}
          approveLabel={approveLabel}
        />
      </div>
    </div>
  );
}
